import {
  Anchor,
  Button,
  Icon,
  Skeleton,
  Text,
} from '@cubeyou-team/hx-react-components';
import React, { FC, useCallback } from 'react';
import { AI_GRADIENT_PROPERTIES } from '@discover-frontend/constants';

import translations from 'src/lang';
import { useOpenCreateSupportTicketModal } from 'src/hooks/useOpenCreateSupportTicketModal';
import { STANLEY_QUERY_LEARN_MORE_URL } from 'src/modules/query-builder/components/stanley-query/constants';
import { useStanleyQueryPanel } from '../../../../providers/StanleyQueryPanelContext';

const { STANLEY_QUERY: translation } = translations['en-US'];

interface StanleyQueryFooterProps {
  isLoading: boolean;
  isNewQuery: boolean;
  onPrimaryClick: React.MouseEventHandler<HTMLButtonElement>;
}

export const StanleyQueryFooter: FC<StanleyQueryFooterProps> = ({
  isLoading,
  isNewQuery = false,
  onPrimaryClick,
}) => {
  const openCreateSupportTicket = useOpenCreateSupportTicketModal();
  const {
    creditsRemaining,
    isCreditsFree,
    isCreditsLoading,
    isCreditsError,
    isCreditsExpired,
  } = useStanleyQueryPanel();

  const handleContactCsmClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      openCreateSupportTicket();
    },
    [openCreateSupportTicket]
  );

  const renderCreditsLabel = () => {
    if (isCreditsFree) return null;

    if (isCreditsLoading) {
      return (
        <span className="inline-flex items-center gap-1">
          <Skeleton speed={2} width={80} height={16} viewBox="0 0 80 16">
            <rect x="0" y="4" rx="4" ry="4" width="24" height="10" />
          </Skeleton>
        </span>
      );
    }

    const creditsValue =
      isCreditsError || creditsRemaining == null ? '—' : creditsRemaining;

    return `${creditsValue} ${translation.MODAL.CREDITS_INFO}`;
  };

  return (
    <div className="flex items-center justify-between py-6">
      <div className="flex items-center gap-2">
        <Icon
          icon="icon-polar"
          size="medium"
          gradient={AI_GRADIENT_PROPERTIES}
        />
        {!isCreditsFree && (
          <Text size="small" variant="dark_lighter">
            {renderCreditsLabel()}
          </Text>
        )}
        <Anchor href="#" size="small" onClick={handleContactCsmClick}>
          {translation.MODAL.CONTACT_INFO}
        </Anchor>
      </div>
      <div className="flex items-center gap-2">
        <Text size="small">{translation.MODAL.AI_WARNING}</Text>
        <Anchor
          href={STANLEY_QUERY_LEARN_MORE_URL}
          size="small"
          target="_blank"
        >
          {translation.MODAL.LEARN_MORE}
        </Anchor>
        {isNewQuery ? (
          <Button
            type="button"
            icon="icon-ai"
            size="small"
            variant="secondary"
            gradient={AI_GRADIENT_PROPERTIES}
            disabled={isLoading}
            onClick={onPrimaryClick}
          >
            {translation.BUTTON.NEW_QUERY}
          </Button>
        ) : (
          <Button
            size="small"
            variant="primary"
            className="w-32"
            gradient={AI_GRADIENT_PROPERTIES}
            disabled={isLoading || isCreditsExpired}
            onClick={onPrimaryClick}
          >
            {translation.BUTTON.GO}
          </Button>
        )}
      </div>
    </div>
  );
};
